import { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { SitePlacement } from '../../utils/basemap';
import styles from './SitePlacementPanel.module.css';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const DEFAULT_CENTER: [number, number] = [44.0, 17.5];

export interface SceneAnchor { x: number; y: number; z: number }

interface SitePlacementPanelProps {
  initial: SitePlacement | null;
  // Centre and ground of what is on in the scene: the point that goes under the pin.
  anchor: SceneAnchor | null;
  defaultLatLng: { lat: number; lng: number } | null;
  onPreview: (placement: SitePlacement | null) => void;
  onSave: (placement: SitePlacement | null) => void;
  onClose: () => void;
}

const RecenterMap = ({ position }: { position: [number, number] }) => {
  const map = useMap();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    map.panTo(position);
  }, [position, map]);
  return null;
};

const PinMarker = ({ position, onChange }: { position: [number, number]; onChange: (lat: number, lng: number) => void }) => {
  const ref = useRef<L.Marker>(null);
  useMapEvents({ click(e) { onChange(e.latlng.lat, e.latlng.lng); } });
  return (
    <Marker
      ref={ref}
      position={position}
      draggable
      eventHandlers={{ dragend() { const p = ref.current?.getLatLng(); if (p) onChange(p.lat, p.lng); } }}
    />
  );
};

// Pin + rotation for a scene with no coordinate system. The scene keeps
// drawing behind the panel, so the map under it follows every change.
const SitePlacementPanel = ({ initial, anchor, defaultLatLng, onPreview, onSave, onClose }: SitePlacementPanelProps) => {
  const [latLng, setLatLng] = useState<{ lat: number; lng: number } | null>(
    initial ? { lat: initial.lat, lng: initial.lng } : defaultLatLng,
  );
  const [heading, setHeading] = useState(initial?.heading ?? 0);
  const origin = initial && !anchor
    ? { originX: initial.originX, originY: initial.originY, originZ: initial.originZ }
    : anchor
      ? { originX: anchor.x, originY: anchor.y, originZ: anchor.z }
      : null;
  const placement: SitePlacement | null = latLng && origin ? { lat: latLng.lat, lng: latLng.lng, heading, ...origin } : null;
  const sig = JSON.stringify(placement);
  const previewRef = useRef(onPreview);
  previewRef.current = onPreview;
  useEffect(() => {
    const t = window.setTimeout(() => previewRef.current(placement), 250);
    return () => window.clearTimeout(t);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const position: [number, number] = latLng ? [latLng.lat, latLng.lng] : DEFAULT_CENTER;
  return (
    <div className={styles.root}>
      <p className={styles.hint}>
        Drop the pin where the centre of the scanned area is, then rotate until the scan lines up
        with the map behind this panel.
      </p>
      <div className={styles.mapWrap}>
        <MapContainer center={position} zoom={latLng ? 16 : 6} className={styles.map} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {latLng && <RecenterMap position={position} />}
          <PinMarker position={position} onChange={(lat, lng) => setLatLng({ lat, lng })} />
        </MapContainer>
      </div>
      <div className={styles.coords}>
        {latLng ? `${latLng.lat.toFixed(5)}, ${latLng.lng.toFixed(5)}` : 'Click the map to drop the pin'}
      </div>
      <label className={styles.row}>
        <span className={styles.rowLabel}>Rotate</span>
        <input
          type="range" min={0} max={360} step={0.5}
          className={styles.range}
          value={heading}
          onChange={(e) => setHeading(parseFloat(e.target.value))}
        />
        <input
          type="number" min={0} max={360} step={0.5}
          className={styles.number}
          value={Math.round(heading * 10) / 10}
          onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) setHeading(((v % 360) + 360) % 360); }}
        />
        <span className={styles.unit}>°</span>
      </label>
      {!anchor && !initial && <p className={styles.warn}>Switch on a point cloud, model or drawing first — the pin marks the centre of what is on.</p>}
      <div className={styles.actions}>
        {initial && (
          <button type="button" className={styles.ghost} onClick={() => onSave(null)}>
            {defaultLatLng ? 'Back to the project pin' : 'Remove placement'}
          </button>
        )}
        <span className={styles.spacer} />
        <button type="button" className={styles.ghost} onClick={onClose}>Cancel</button>
        <button type="button" className={styles.primary} disabled={!placement} onClick={() => placement && onSave(placement)}>Apply</button>
      </div>
    </div>
  );
};

export default SitePlacementPanel;
